<template>
  <div class="sso-login-page">
    <div id="loadingBox">
      <Loader
        v-if="isLoading"
        :is-processing="true"
        :loader-text="loadingText"
        data-testid="sso-login-loader"
      />
      <div v-if="error" class="text-center">
        <p
          class="sso-login-page__error"
          role="alert"
          data-testid="sso-login-error"
        >
          {{ error }}
        </p>
        <button
          class="btn bg-gradient-blue text-white"
          @click="reinitiateLogin"
        >
          Login
        </button>
      </div>
    </div>
    <div id="redirectBox">
      <div
        v-if="mobileApp"
        class="d-flex flex-column flex-wrap align-items-center gap-4"
      >
        <div
          v-if="mobileApp === 'pass' || mobileApp === 'both'"
          class="d-flex flex-column px-4 flex-wrap align-items-center gap-4"
        >
          <div class="base-blue-text text-center" v-if="mobileApp === 'both'">
            If you are logging into the Securly Pass App please click here
          </div>
          <a
            class="btn bg-gradient-blue text-white"
            :href="redirectToMobileApp('pass')"
          >
            Go To PASS App
          </a>
        </div>
        <div
          v-if="mobileApp === 'visitor' || mobileApp === 'both'"
          class="d-flex flex-column px-4 flex-wrap align-items-center gap-4"
        >
          <div class="base-blue-text text-center" v-if="mobileApp === 'both'">
            If you are logging into the Securly Visitor App please click here
          </div>
          <a
            class="btn bg-gradient-blue text-white"
            :href="redirectToMobileApp('visitor')"
          >
            Go To Visitor App
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import localforage from "localforage"
import securlyPlatformConfig from "@/configs/securlyPlatformConfig.js"
import { LOGIN_ROUTE_PATH } from "@/configs/authLoginConfig.js"
import {
  completeWebLogin,
  loadSecurlyPlatformScript
} from "@/helpers/ssoLoginHelper.js"
import randomstring from "randomstring"
import sha256 from "crypto-js/sha256"
import CryptoJS from "crypto-js"
export default {
  name: "SsoLogin",

  components: {
    Loader
  },

  setup() {
    const route = useRoute()
    const store = useStore()
    const loadingText = ref("Loading...")
    const isLoading = ref(true)
    const error = ref(null)
    const ssoRedirected = ref(true)
    const mobileApp = ref(null)
    const mobileRedirectQuery = ref(null)

    /**
     * Supported urls:
     * /login -> web securly-sso login
     * /login?mobile_redirect -> request from mobile app (Visitor or Pass old version)
     * /login?mobile_redirect&app=pass -> request from mobile app (Pass new version)
     * /login?mobile_redirect&app=visitor -> request from mobile app (Visitor new version)
     *  Once we get mobile_redirect, securly-sso login will happen and sso-login will be redirected to the
     * /login/mobile with the original query params, which will be used to redirect to the mobile app
     */

    onMounted(() => {
      if (String(route.name).toLowerCase() !== "login") {
        // jump to sso with force_auth_mech
        redirectLoginToSource(route.name)
        return
      }
      if (route.query?.mobile_redirect) {
        // Retrieve the additional query params and value from the query string except mobile_redirect
        const additionalQueryParams = Object.entries(route.query)
          .filter(([key]) => key !== "mobile_redirect")
          .map(([key, value]) => `${key}=${value}`)
          .join("&")
        startSsoLogin(true, additionalQueryParams)
        return
      }

      if (route.query?.code) {
        const routeState = route.query?.state
        // check if the state is string or int then its value should be < 9999
        if (routeState < 9999) {
          // This is simulate login
          ssoRedirected.value = false
        } else {
          // This is normal login
          ssoRedirected.value = true
        }
      } else {
        ssoRedirected.value = true
      }

      if (ssoRedirected.value) {
        startSsoLogin()
        return
      }

      const state = route.query.state ?? getProviderIDByName(route.params.type)
      const redirectUrl = getRedirectUri(route.params.source, route.params.type)
      isLoading.value = true
      const params = {
        ...route.query,
        redirectUri: redirectUrl,
        state: state,
        isMobile: route.params.source === "mobile"
      }
      startSimulateLogin(params)
    })

    const startSsoLogin = async (
      forMobile = false,
      additionalQueryParams = "",
      forceAuthMech = null
    ) => {
      await localforage.clear().catch(() => null)

      let redirectUri = `${window.location.origin}` + LOGIN_ROUTE_PATH
      if (forMobile) {
        redirectUri = redirectUri + "/mobile?" + additionalQueryParams
      }

      let auth = null

      try {
        auth = await loadSecurlyPlatformScript()
        if (!auth) {
          error.value = "Unable to load sign-in."
          isLoading.value = false
          return
        }

        await auth.Init(
          securlyPlatformConfig.clientId,
          securlyPlatformConfig.issuer
        )

        try {
          await auth.Identify()
        } catch {
          loadingText.value = "Redirecting..."
          auth.Login(redirectUri, forceAuthMech)
          return
        }

        let loginForMobile =
          route.params.source === "mobile" || route.query?.mobile_redirect
        let additionalParams = { ...route.query }
        try {
          loadingText.value = "Signing in..."
          const response = await store.dispatch(
            "authentication/externalLogin",
            {
              isMobile: loginForMobile,
              securlysso: true
            }
          )

          isLoading.value = false
          if (loginForMobile) {
            afterLoginMobile(
              response?.data?.access_token,
              null,
              additionalParams
            )
          } else {
            completeWebLogin(store, response?.data)
          }
        } catch (err) {
          error.value = formatSsoLoginError(err)
          isLoading.value = false
        }
      } catch {
        window.location.reload()
      }
    }

    const afterLoginMobile = (token, err, additionalParams = null) => {
      mobileRedirectQuery.value = token ? "?token=" + token : "?error=" + err
      let skipAutoRedirect = additionalParams?.skip_click === "false"
      let app = "both"
      if (additionalParams?.app == "pass") {
        app = "pass"
      } else if (additionalParams?.app == "visitor") {
        app = "visitor"
      }
      // new builds will set app as pass/visitor but old builds never send the params
      // so it is set as both as per existing logic
      const isLegacyMobileApp = app === "both"
      if (!skipAutoRedirect && !isLegacyMobileApp) {
        window.location.href = redirectToMobileApp(app)
      }
      mobileApp.value = skipAutoRedirect || isLegacyMobileApp ? app : null
    }

    const startSimulateLogin = async (params) => {
      try {
        const response = await store.dispatch(
          "authentication/externalLogin",
          params
        )
        const data = response?.data

        if (route.params.source === "mobile") {
          afterLoginMobile(data?.access_token)
        } else {
          completeWebLogin(store, data)
        }
        isLoading.value = false
      } catch (err) {
        isLoading.value = false
        if (route.params.source === "mobile") {
          afterLoginMobile(null, err?.message)
        } else {
          error.value = formatSsoLoginError(err)
        }
      }
    }

    const reinitiateLogin = () => {
      isLoading.value = true
      error.value = null
      loadingText.value = ""
      store.dispatch("authentication/unauthorize")
      store.dispatch("authentication/finishSecurlySession")
    }

    const formatSsoLoginError = (err) => {
      if (err?.message) {
        return err.message
      }
      if (Array.isArray(err?.errors) && err.errors.length) {
        return err.errors.join(", ")
      }
      return "Unable to sign in."
    }

    const redirectToMobileApp = (app) => {
      if (app === "pass") {
        return (
          import.meta.env.VITE_MOBILE_URL + "login" + mobileRedirectQuery.value
        )
      } else if (app === "visitor") {
        return (
          import.meta.env.VITE_VISITOR_MOBILE_URL +
          "login" +
          mobileRedirectQuery.value
        )
      } else {
        console.error("Invalid app") //eslint-disable-line
      }
    }
    const getProviderIDByName = (name) => {
      const providers = {
        clever: 102,
        classlink: 101
      }
      return providers[name]
    }
    const getPKCE = (random = true) => {
      let code_verifier = randomstring.generate(128)

      if (!random) {
        const constant_verifier = import.meta.env.VITE_VERIFIER
        const dateString = new Date()
          .toISOString()
          .slice(0, 10)
          .replace("-", "")
          .replace("-", "")
        code_verifier = btoa(
          constant_verifier.substring(0, 50) +
            dateString +
            constant_verifier.substring(50)
        ).substring(0, 128)
      }

      sessionStorage.setItem("code_verifier", code_verifier)
      //const base64Digest = sha256(code_verifier).toString().substring(0, 43)
      //const code_challenge = base64url.fromBase64(base64Digest)
      const code_challenge = sha256(code_verifier)
        .toString(CryptoJS.enc.Base64)
        .replaceAll("+", "-")
        .replaceAll("/", "_")
        .replace(/=+$/, "")

      return code_challenge
    }

    const redirectLoginToSource = (source) => {
      switch (source) {
        case "glogin":
          loginGoogle()
          break

        case "ologin":
          loginMS()
          break

        case "clogin":
          loginClever()
          break

        case "classlinklogin":
          loginClassLink()
          break

        default:
          break
      }
    }
    const loginGoogle = () => {
      startSsoLogin(false, "", "google")
    }

    const loginMS = (source = "web") => {
      const redirect_uri = getRedirectUri(source)
      const url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${
        import.meta.env.VITE_MICROSOFT_CLIENT_ID
      }&redirect_uri=${redirect_uri}&response_type=code&response_mode=query&scope=https%3A%2F%2Fgraph.microsoft.com%2Fuser.read%20openid%20profile%20email%20offline_access&code_challenge=${getPKCE(
        false
      )}&code_challenge_method=S256&state=20`
      window.location.href = url
    }
    const loginClassLink = (source = "web") => {
      const redirect_uri = getRedirectUri(source, "classlinklogin")
      const url = `https://launchpad.classlink.com/oauth2/v2/auth?scope=profile&redirect_uri=${redirect_uri}&client_id=${import.meta.env.VITE_CLASSLINK_CLIENT_ID}&response_type=code&state=101`
      window.location.href = url
    }
    const loginClever = (source = "web") => {
      const redirect_uri = getRedirectUri(source, "clever")
      // https://clever.com/in/protective-information-7564
      const url = `https://clever.com/oauth/authorize?client_id=${import.meta.env.VITE_CLEVER_CLIENT_ID}&redirect_uri=${redirect_uri}&response_type=code&state=102`
      window.location.href = url
    }
    const getRedirectUri = (source = "web", type = null) => {
      const typePath = type ? `/${type}` : ""
      return `${document.location.origin}${LOGIN_ROUTE_PATH}/${source}${typePath}`
    }

    return {
      isLoading,
      loadingText,
      error,
      mobileApp,
      reinitiateLogin,
      redirectToMobileApp
    }
  }
}
</script>

<style scoped lang="scss">
@import "src/assets/scss/_variables.scss";

.sso-login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sso-login-page__error {
  max-width: 420px;
  margin: 0 1rem;
  text-align: center;
  color: $form-red;
}
</style>
